import {
	GenerateSAS,
	CheckIfSasIsValid,
	UploadFileInStorage,
} from "../../../../services/uploadHelpers";
import {
	Uuidv4,
	GetFileExtension,
	DisplayMessage,
} from "../../../../services/helpers";
import { parseMediaFileType } from "../utils/constants";
import { deleteMediaFile } from "./mediaLibraryClient";

class MediaUploadService {
	constructor(module, resources = {}) {
		this.module = module;
		this.resources = resources;
	}

	getEntityInfo() {
		const entityMappings = {
			messageTemplates: {
				entityType: "messageTemplate",
			},
			signableDocuments: {
				entityType: "signableDocument",
			}
			// Add other modules as needed
		};

		if (!entityMappings[this.module]) {
			throw new Error(`No entity mapping found for module: ${this.module}`);
		}

		return entityMappings[this.module];
	}

	prepareFiles(files) {
		return files.map((file) => ({
			file,
			extension: GetFileExtension(file.name),
			id: Uuidv4(),
			originalName: file.name,
		}));
	}

	async generateSasTokens(fileInfos) {
		const { entityType, entityValue } = this.getEntityInfo();

		const fileNames = fileInfos.map((info) => `${info.id}.${info.extension}`);

		const sasRequest = JSON.stringify({
			minutes: 5,
			entityType: entityType,
			entityValue: entityValue ?? undefined,
			fileNames: fileNames,
		});

		const sasTokens = await GenerateSAS(sasRequest);

		if (!CheckIfSasIsValid(sasTokens)) {
			throw new Error(this.resources["ErrorGeneratingSAS"]);
		}

		return sasTokens;
	}

	async uploadFile(file, sasToken, originalName) {
		try {
			const returnWholeSas = true;
			const sas = await UploadFileInStorage(
				file,
				sasToken,
				file.type,
				this.resources["ErrorOnLoadingFile"],
				returnWholeSas
			);

			if (!sas) {
				return null;
			}

			return {
				blobRelativePath: sas.blobRelativePath,
				type: parseMediaFileType(file.type),
				fileName: originalName,
				originalName: originalName,
				blobUri: sas.blobUri,
			};
		} catch (error) {
			console.error("Upload error:", error);
			DisplayMessage(
				`${this.resources["UploadError"]}: ${error.message}`,
				"error"
			);
			return null;
		}
	}

	async uploadFiles(files) {
		if (!files || files.length === 0) {
			return [];
		}

		try {
			const fileInfos = this.prepareFiles(files);

			const sasTokens = await this.generateSasTokens(fileInfos);

			const uploadResults = await Promise.all(
				fileInfos.map((fileInfo, index) =>
					this.uploadFile(
						fileInfo.file,
						sasTokens[index],
						fileInfo.originalName
					)
				)
			);

			return uploadResults.filter((result) => result !== null);
		} catch (error) {
			DisplayMessage(
				`${this.resources["UploadProcessError"]}: ${error.message}`,
				"error"
			);
			return [];
		}
	}

	async deleteMediaFiles(mediaFileIds) {
		if (!mediaFileIds || mediaFileIds.length === 0) {
			return {
				success: false,
				error: this.resources["NoFilesSelectedForDeletion"],
			};
		}

		try {
			const { entityType } = this.getEntityInfo();

			const deletePromises = mediaFileIds.map((id) =>
				deleteMediaFile(id, entityType)
			);

			const responses = await Promise.all(deletePromises);

			const allSuccessful = responses.every((response) => response.ok);

			if (!allSuccessful) {
				return {
					success: false,
					error: this.resources["MediaFilesDeleteError"],
				};
			}

			return { success: true };
		} catch (error) {
			return {
				success: false,
				error: error.message,
			};
		}
	}
}

export default MediaUploadService;
